<template>
  <!--
    A real <button>: on a phone the whole card is the tap target, and a button is what a screen
    reader and a keyboard both already understand. Everything inside is a <span> because a button
    may only contain phrasing content. No aria-label on purpose — it would REPLACE the content for
    a screen reader, and the content (including each alert's own label) is what should be read.
  -->
  <button
    type="button"
    class="sv-mcard"
    :class="[`sv-mcard--${bucket}`, { 'sv-mcard--broken': broken }]"
    :style="{ borderLeftColor: accent }"
    :data-test="`sv-card-${appt.AptNum}`"
    @click="$emit('select', appt)"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
  >
    <span
      class="sv-mcard__avatar"
      :class="`sv-mcard__avatar--${bucket}`"
      :style="bucket === 'arrived' || bucket === 'completed' ? null : { background: tint }"
      aria-hidden="true"
    >
      {{ initials }}
      <span v-if="bucket === 'arrived'" class="sv-mcard__ring"></span>
    </span>

    <span class="sv-mcard__body">
      <!--
        ⛔ The name has this row to itself. The first build put the confirmation wording here too,
        as the grid block does, and on a real 390px phone (with Teamio's own sidebar taking 47px)
        that cut "Danielson, Colette" to "Danielson,…". A card can grow downwards; the block cannot,
        so on the card the status moves to the next row instead of competing with the name.
      -->
      <span class="sv-mcard__row1">
        <span class="sv-mcard__dot" :class="`sv-mcard__dot--${bucket}`" aria-hidden="true"></span>
        <span class="sv-mcard__name" data-test="sv-card-name">{{ name }}</span>
        <span v-if="broken" class="sv-mcard__broken" data-test="sv-card-broken">Broken</span>
        <!--
          The visit checklist's entry point on a touch screen (a phone, or an iPad held upright
          with the timeline). Nothing is drawn with a mouse (hover opens the card) or when the visit
          has nothing in its checklist. Its tap never reaches the card's own, which opens the panel.
        -->
        <ChecklistChip
          big
          class="sv-mcard__chip"
          :totals="checklistTotals"
          :category-count="categoryCount"
          :mode="checklistMode"
          @toggle="onChipToggle"
        />
      </span>

      <span class="sv-mcard__meta">
        <span class="sv-mcard__time" data-test="sv-card-time">{{ timeRange }}</span>
        <span
          class="sv-mcard__op"
          :style="{ background: tint, boxShadow: `inset 3px 0 0 ${accent}` }"
          data-test="sv-card-op"
        >{{ opName }}</span>
        <span v-if="providerLabel" class="sv-mcard__prov" :title="providerTitle">{{ providerLabel }}</span>
        <span
          v-if="showStatus && !broken"
          class="sv-mcard__status"
          :class="`sv-mcard__status--${bucket}`"
          data-test="sv-card-status"
        >{{ statusText }}</span>
      </span>

      <span v-if="showProcedures" class="sv-mcard__procs" data-test="sv-card-procs">{{ appt.ProcDescript }}</span>

      <span v-if="alertKeys.length" class="sv-mcard__alerts" data-test="sv-card-alerts">
        <AlertBadge
          v-for="key in alertKeys"
          :key="key"
          :alert-key="key"
          :detail="alertDetails[key]"
          :hover-mode="hoverMode"
          @hover-alert="onHoverAlert"
        />
      </span>
    </span>
  </button>
</template>

<script>
/**
 * One appointment on the phone timeline (Phase 6) — the phone's AppointmentBlock.
 *
 * The block has to fit a height set by the appointment's length, so it spends a lot of code on
 * what to DROP. A card has no such limit: it grows to fit, so it always shows the full name, the
 * time, the chair, the procedures and every alert. The rules for WHAT is shown are the block's
 * rules, read from the same helpers, so a phone and an iPad never disagree about a patient.
 *
 * Tapping the card opens the Patient Panel (Phase 7), the same as a block on the grid; the visit
 * checklist (Phase 10e) has its own chip, because a card is one big button and cannot also be two.
 *
 * ⛔ No prop may be named `patNum`, `siteID` or `loading`: mixin.js defines computeds with those
 * names and a computed SILENTLY REPLACES a same-named prop. Guarded by
 * cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import { resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';
import {
  appointmentSpan, formatTimeRange, patientDisplayName, patientInitials,
} from '@/utils/scheduleView/layout';
import { alertsForPatient, blockAlertKeys } from '@/utils/scheduleView/alerts';
import { ALERT_KEYS } from '@/utils/scheduleView/settingsForm';
import AlertBadge from './AlertBadge.vue';
import ChecklistChip from './ChecklistChip.vue';

export default {
  name: 'AppointmentCard',

  components: { AlertBadge, ChecklistChip },

  props: {
    /** One appointment from /schedule-view/day. */
    appt: { type: Object, required: true },
    /** Resolved settings from the same payload. Never a local default — see the store. */
    viewSettings: { type: Object, required: true },
    /** DefNum -> bucket, already merged with the office's overrides. */
    bucketMap: { type: Object, default: () => ({}) },
    /** The whole /schedule-view/alerts payload, or null before it arrives. See AppointmentBlock. */
    alertPayload: { type: Object, default: null },
    /** This chair's Soft Pastels pair — the same colours its column has on the grid. */
    tint: { type: String, default: '#eef1ee' },
    accent: { type: String, default: '#9ca4a2' },
    /** The chair, named, because a single column cannot say it by position. */
    opName: { type: String, default: '' },
    /** Short provider label ("PP") and its full name for the tooltip. */
    providerLabel: { type: String, default: '' },
    providerTitle: { type: String, default: '' },
    /** This appointment's visit checklist from the page ({ categories, totals }), or null. */
    checklist: { type: Object, default: null },
    /** 'hover' | 'pop' | 'sheet', from the layer. */
    checklistMode: { type: String, default: 'hover' },
  },

  computed: {
    bucket() {
      return resolveBucket(this.appt, this.bucketMap);
    },

    broken() {
      return isBroken(this.appt);
    },

    statusText() {
      return statusLabel(this.appt, this.bucket);
    },

    showStatus() {
      return !!this.viewSettings.showConfirmation && !!this.statusText;
    },

    showProcedures() {
      return !!this.viewSettings.showProcedures && !!this.appt.ProcDescript;
    },

    name() {
      return patientDisplayName(this.appt);
    },

    initials() {
      return patientInitials(this.appt);
    },

    timeRange() {
      const span = appointmentSpan(this.appt);
      return span ? formatTimeRange(span.start, span.end) : '';
    },

    /**
     * The block's rule — the SAME function (alerts.js blockAlertKeys): switched on by the office
     * AND actually computed by the server. ⛔ Not re-filtered here; see the note on the block.
     */
    alertKeys() {
      return blockAlertKeys(this.alertPayload, this.appt.PatNum, this.viewSettings, ALERT_KEYS);
    },

    alertDetails() {
      return alertsForPatient(this.alertPayload, this.appt.PatNum);
    },

    hoverMode() {
      return this.checklistMode === 'hover';
    },

    checklistTotals() {
      return (this.checklist && this.checklist.totals) || { todo: 0, done: 0, fyi: 0 };
    },

    categoryCount() {
      return this.checklist && this.checklist.categories ? this.checklist.categories.length : 0;
    },
  },

  beforeDestroy() {
    // A card removed while the pointer is on it never gets a mouseleave (see AppointmentBlock).
    this.$emit('card-leave', { appt: this.appt });
  },

  methods: {
    // A narrow DESKTOP window gets the timeline but still has a mouse: hover opens the checklist
    // there too (the layer ignores these on touch, where the chip is the way in).
    onEnter() {
      this.$emit('card-enter', { appt: this.appt, el: this.$el, via: 'hover' });
    },

    onLeave() {
      this.$emit('card-leave', { appt: this.appt });
    },

    onHoverAlert(key) {
      this.$emit('alert-focus', { appt: this.appt, key });
    },

    onChipToggle() {
      this.$emit('open-checklist', { appt: this.appt, el: this.$el });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-mcard {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  // Tighter than the design's 12px: on a 390px phone Teamio's sidebar already takes 47px, and
  // every pixel given back here goes to the patient's name.
  padding: 10px 10px 10px 8px;
  background: $sv-surface;
  border: 1px solid $sv-line;
  border-left: 4px solid $sv-ink-3;
  border-radius: $sv-radius-lg;
  box-shadow: 0 1px 2px rgba(21, 32, 26, 0.06);
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active { background: $sv-bg; }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand;
    outline-offset: 1px;
  }
}

.sv-mcard--broken {
  border-style: dashed;
  background: #fbfbfa;

  .sv-mcard__name {
    text-decoration: line-through;
    text-decoration-color: rgba(194, 65, 12, 0.6);
  }
}

.sv-mcard__avatar {
  position: relative;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: $sv-ink-2;

  // Arrived and completed take over the avatar's colour, per the design: arrived is the one state
  // somebody at the front desk is actively waiting on, and completed should recede.
  &--arrived {
    background: #d6e6f7; // $sv-status-arrived at 18% on white
    color: $sv-status-arrived;
  }

  &--completed {
    background: #eceeec;
    color: $sv-ink-3;
    opacity: 0.75;
  }
}

.sv-mcard__ring {
  position: absolute;
  top: -3px;
  right: -3px;
  bottom: -3px;
  left: -3px;
  border: 2px solid $sv-status-arrived;
  border-radius: 50%;
  animation: sv-mcard-pulse 2s ease-in-out infinite;
}

@keyframes sv-mcard-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.06); }
}

@media (prefers-reduced-motion: reduce) {
  .sv-mcard__ring { animation: none; }
}

.sv-mcard__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sv-mcard__row1 {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}

.sv-mcard__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  // Centred on the name's FIRST line, which may now wrap onto a second.
  margin-top: 6px;
  border-radius: 50%;
  background: $sv-status-unconfirmed;

  &--confirmed { background: $sv-status-confirmed; }
  &--arrived { background: $sv-status-arrived; }
  &--completed { background: $sv-status-completed; }
  &--unconfirmed { background: $sv-status-unconfirmed; }
}

// The name owns its row (see the template note) and WRAPS rather than truncating: a card can
// grow downwards, and on a 320px phone an ellipsis cut "Danielson, Colette" to "Danielson, Col…".
// Two lines, then an ellipsis, so one very long name cannot swallow the card.
.sv-mcard__name {
  flex: 1 1 auto;
  min-width: 80px;
  font-size: 14.5px;
  line-height: 1.35;
  font-weight: 700;
  color: $sv-ink;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// The chip is the right-hand end of the name's row, top-aligned with the (possibly wrapped) name.
.sv-mcard__chip {
  align-self: flex-start;
  margin-left: auto;
}

// Last on the meta row, so it wraps onto a line of its own before it ever shortens anything else.
.sv-mcard__status {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: $sv-status-unconfirmed;

  &--confirmed { color: $sv-status-confirmed; }
  &--arrived { color: $sv-status-arrived; }
  &--completed { color: $sv-status-completed; }
  &--unconfirmed { color: $sv-status-unconfirmed; }
}

.sv-mcard__broken {
  flex: 0 0 auto;
  margin-top: 2px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fdeee6;
  color: $sv-broken;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.sv-mcard__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
  font-size: 12px;
  color: $sv-ink-3;
}

.sv-mcard__time {
  font-weight: 600;
  color: $sv-ink-2;
  white-space: nowrap;
}

.sv-mcard__op {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px 7px 1px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: $sv-ink-2;
}

.sv-mcard__prov {
  font-size: 11px;
  font-weight: 700;
  color: $sv-ink-3;
  white-space: nowrap;
}

.sv-mcard__procs {
  font-size: 12.5px;
  line-height: 1.35;
  color: $sv-ink-2;
  // Two lines, then ellipsis — the same limit as the block, so a long treatment list does not turn
  // one card into half a screen.
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sv-mcard__alerts {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 2px;

  // The grid's 15px icons are sized for a 22px block. On a card read at arm's length they are
  // enlarged, and given a fill so they read as badges on a white card rather than loose glyphs.
  ::v-deep .sv-badge {
    width: 22px;
    height: 22px;
    font-size: 12px;
    border-radius: 5px;
    background: $sv-bg;
    border: 1px solid $sv-line;
  }
}
</style>
